import { Box, Flex, Heading } from '@radix-ui/themes';
import { SessionRecapHelper } from 'classes/helpers/session-recap.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import {
  PlotHoverProvider,
  usePlotHover,
} from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/plot-hover';
import { SessionRecapPitchCard } from 'components/sections/analytics/session-hits/recap/pitch-card';
import { SessionRecapStatsBar } from 'components/sections/analytics/session-hits/recap/stats-bar';
import { SessionRecapVisualizations } from 'components/sections/analytics/session-hits/recap/visualizations';
import { SessionHitsTableNew } from 'components/sections/analytics/session-hits/table-new';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { useSessionRecapHits } from 'components/sections/analytics/store/use-session-recap-hits';
import { HittersContext } from 'contexts/hitters.context';
import { t } from 'i18next';
import { HitterSide } from 'lib_ts/enums/hitters.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ISessionHit } from 'lib_ts/interfaces/session-hits/i-session-hit';
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './index.scss';

interface IProps {
  header: ReactNode;
  sessionID: string;
  hitterID: string;
  machineID: string | undefined;
}

/** highlights linked rows without re-rendering the (memoized) table body */
const LinkedRowsStyle = () => {
  const { hoveredItemKey } = usePlotHover();
  const selectedHitID = useAnalyticsStore((s) => s.selectedHitID);
  const hoveredHitID = hoveredItemKey ?? null;

  const rule = (id: string | null, declaration: string) =>
    id
      ? `.SessionRecapTable tr[data-row-id="${CSS.escape(id)}"] { ${declaration} }`
      : '';

  return (
    <style>
      {[
        rule(selectedHitID, 'background-color: var(--accent-a4);'),
        rule(hoveredHitID, 'background-color: var(--accent-a3);'),
      ].join('\n')}
    </style>
  );
};

const SessionRecapContent = (props: IProps) => {
  const { hitterID } = props;
  const { hoverHandlers, hoveredItemKey } = usePlotHover();
  const handlersRef = useRef(hoverHandlers);
  handlersRef.current = hoverHandlers;
  const rowHoverID = useRef<string | undefined>();

  const loadingHits = useAnalyticsStore((s) => s.loadingHits);
  const loading = useAnalyticsStore((s) => s.loadingSessions || s.loadingHits);
  const { filtered, byID, szDef } = useSessionRecapHits();

  const colorByID = useAnalyticsStore((s) => s.colorByID);
  const legendKeys = useAnalyticsStore((s) => s.legendKeys);
  const zoneFilter = useAnalyticsStore((s) => s.zoneFilter);

  const { hitters } = useContext(HittersContext);
  const hitterSide =
    hitters.find((h) => h._id === hitterID)?.side ?? HitterSide.RHH;

  // the table's current sort order, so the pitch card can step through it
  const [sortedHits, setSortedHits] = useState<ISessionHit[]>([]);
  const cardHits = useMemo(() => {
    if (!legendKeys && !zoneFilter) {
      return sortedHits;
    }

    return sortedHits.filter((hit) => {
      const record = byID[hit._id];

      if (!record) {
        return false;
      }

      if (legendKeys) {
        const key = SessionRecapHelper.getLegendKey(record, colorByID);

        if (!key || !legendKeys.includes(key)) {
          return false;
        }
      }

      return (
        !zoneFilter ||
        SessionRecapHelper.isInChartZone(record, zoneFilter, hitterSide, szDef)
      );
    });
  }, [sortedHits, byID, colorByID, legendKeys, zoneFilter, hitterSide, szDef]);

  const handleOpenHit = useCallback(
    (hit: ISessionHit) =>
      useAnalyticsStore.setState({ selectedHitID: hit._id }),
    []
  );

  const handleHoverHit = useCallback((hit: ISessionHit | undefined) => {
    if (hit) {
      rowHoverID.current = hit._id;
      handlersRef.current(hit._id).onMouseEnter?.();
      return;
    }

    handlersRef.current(rowHoverID.current).onMouseLeave?.();
  }, []);

  useEffect(() => {
    if (loadingHits && hoveredItemKey) {
      handlersRef.current(hoveredItemKey).onMouseLeave?.();
    }
  }, [loadingHits, hoveredItemKey]);

  return (
    <ErrorBoundary identifier="SessionRecapContent">
      <Box className={`SessionRecap ${RADIX.VFLEX.SCROLLABLE}`}>
        <Flex direction="column" gap={RADIX.FLEX.GAP.SECTION} pb="4">
          {props.header}

          <SessionRecapStatsBar loading={loading} records={filtered} />

          <SessionRecapVisualizations
            hitterID={hitterID}
            records={filtered}
            szDef={szDef}
          />

          <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
            <Heading size={RADIX.HEADING.SIZE.SM}>
              {t('hitters.recap-pitch-by-pitch')}
            </Heading>

            <Box className="SessionRecapTable">
              <LinkedRowsStyle />
              <SessionHitsTableNew
                identifier="SessionHitsTableNew"
                sessionID={props.sessionID}
                hitterID={hitterID}
                machineID={props.machineID}
                skipHeader
                onOpenHit={handleOpenHit}
                onHoverHit={handleHoverHit}
                onSortedHitsChange={setSortedHits}
                vFlex={false}
                disableScrolling
                verbose
              />
            </Box>
          </Flex>
        </Flex>
      </Box>

      <SessionRecapPitchCard sortedHits={cardHits} byID={byID} szDef={szDef} />
    </ErrorBoundary>
  );
};

export const SessionRecap = (props: IProps) => (
  <ErrorBoundary identifier="SessionRecap">
    <PlotHoverProvider>
      <SessionRecapContent {...props} />
    </PlotHoverProvider>
  </ErrorBoundary>
);
